import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Select from "react-select";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import {
  buildCondition,
  getCourseName,
  getStudentPackageName,
  getStudentStatusName,
} from "../../static/hook";
import { stStatusOptions } from "../../static/dropdown";
import useFetchBaseQuery from "../../api/baseQueries";
import { fetchDataByRawQuery } from "../../service/dataExport";
import { ConfigProvider, Pagination } from "antd";
import { useSearchParams } from "react-router-dom";
import NotAuthorize from "../../error/NotAuthorize";

const UniCatDash = () => {
  const [searchParams] = useSearchParams();
  const userCode = parseInt(searchParams.get("userCode"));
  const [authState, setAuthState] = useState(false);
  const fetchBaseQuery = useFetchBaseQuery();
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 50;
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [currentQuery, setCurrentQuery] = useState("");
  const [entriesLimit, setEntriesLimit] = useState(rowsPerPage);
  const hasReportData = Array.isArray(reportData) && reportData.length > 0;
  const [filterDropdowns, setFilterDropdowns] = useState({
    intakeOptions: [],
    locationOptions: [],
    paymentOptions: [],
    statusOptions: [],
    categoryOptions: [],
  });
  const [filters, setFilters] = useState({
    intake: [],
    location: [],
    payment: [],
    status: [],
    ststatus: [],
    category: [],
    package: [],
  });
  const [admType, setAdmType] = useState(null);

  const fetchDropdownOptions = useCallback(async () => {
    const queries = {
      intakeOptions:
        "SELECT IntakeCode AS 'Code', Intakename AS 'Name' FROM intakemas",
      locationOptions:
        "SELECT LocCode AS 'Code', locname AS 'Name' FROM locmas",
      paymentOptions:
        "SELECT DISTINCT PaymentStatus AS Code, PaymentStatus AS Name FROM StudentMas WHERE PaymentStatus IS NOT NULL",
      statusOptions:
        "SELECT DISTINCT status AS Code, status AS Name FROM AdmissionMas",
      categoryOptions:
        "SELECT DISTINCT CatCode AS Code, CatName AS Name FROM CatMas",
    };

    const promises = Object.entries(queries).map(async ([key, query]) => {
      const result = await fetchBaseQuery(query);
      return { key, data: result };
    });

    const resolved = await Promise.all(promises);

    const dropdowns = resolved.reduce((acc, { key, data }) => {
      if (Array.isArray(data)) {
        acc[key] = data.map((item) => ({
          label: item.NAME || "Unknown",
          value: item.CODE || "N/A",
        }));
      }
      return acc;
    }, {});

    setFilterDropdowns(dropdowns);
  }, [fetchBaseQuery]);

  const fetchReportData = useCallback(async () => {
    setLoading(true);
    try {
      const conditions = [
        buildCondition("s.intakeCode", filters.intake, true),
        buildCondition("s.packageCode", filters.package, true),
        buildCondition("s.LocCode", filters.location, true),
        buildCondition("s.PaymentStatus", filters.payment),
        buildCondition("ts.prioritizedStatus", filters.status),
        buildCondition("ra.publicorprivate", filters.admType),
        buildCondition("s.ststatus", filters.ststatus, true),
        buildCondition("s.CatCode", filters.category, true),
      ].filter(Boolean);

      const whereClause = conditions.length
        ? `AND ${conditions.join(" AND ")}`
        : "";

      const query = `
WITH RankedStatus AS (
  SELECT 
    a.StdCode,
    a.status,
    ROW_NUMBER() OVER (
      PARTITION BY a.StdCode
      ORDER BY 
        CASE 
          WHEN a.status = 'Final Admission Accepted' THEN 1
          WHEN a.status = 'Admission Accepted By Student' THEN 2
          WHEN a.status = 'Admission Given' THEN 3
          WHEN a.status = 'Rejected' THEN 4
          WHEN a.status = 'Applied' THEN 5
          WHEN a.status = 'Approved' THEN 6
          WHEN a.status = 'Cancel' THEN 7
          WHEN a.status = 'Dropped by Student' THEN 8
          ELSE 9
        END
    ) AS rn
  FROM AdmissionMas a
),
TopStatus AS (
  SELECT StdCode, status AS prioritizedStatus
  FROM RankedStatus
  WHERE rn = 1
),

AppCounts AS (
  SELECT 
    a.StdCode,
    COUNT(*) AS application,
    SUM(CASE WHEN a.publicorprivate = 'Public' AND a.status IN ('Admission Given', 'Admission Accepted By Student', 'Final Admission Accepted' ) THEN 1 ELSE 0 END) AS publicApplications,
    SUM(CASE WHEN a.publicorprivate = 'Private' AND a.status IN ('Admission Given', 'Admission Accepted By Student', 'Final Admission Accepted' ) THEN 1 ELSE 0 END) AS privateApplications,
    SUM(CASE WHEN a.status IN ('Admission Given', 'Admission Accepted By Student', 'Final Admission Accepted' ) THEN 1 ELSE 0 END) AS nullApplications,
    SUM(CASE WHEN a.status IN ('Rejected') THEN 1 ELSE 0 END) AS rejectedAppl
  FROM AdmissionMas a
  GROUP BY a.StdCode
)

SELECT 
  s.Stdid, 
  lo.LocName, 
  s.StdName, 
  s.PaymentStatus, 
  s.ststatus, 
  s.packageCode,
  cm.CatName, 
  s.uinliststatus,
  COALESCE(ac.application, 0) AS application,
  COALESCE(ac.publicApplications, 0) AS publicApplications,
  COALESCE(ac.privateApplications, 0) AS privateApplications,
  COALESCE(ac.nullApplications, 0) AS nullApplications,
  COALESCE(ac.rejectedAppl, 0) AS rejectedAppl,
  ts.prioritizedStatus
FROM StudentMas s
LEFT JOIN AppCounts ac ON ac.StdCode = s.StdCode
LEFT JOIN TopStatus ts ON ts.StdCode = s.StdCode 
JOIN LocMas lo ON s.LocCode = lo.LocCode 
JOIN CatMas cm ON s.catCode = cm.CatCode 
WHERE s.StdCode IS NOT NULL ${whereClause}
GROUP BY 
  s.Stdid, lo.LocName, s.StdName, s.PaymentStatus, 
  s.ststatus, s.packageCode, cm.CatName, s.uinliststatus,
  ac.application, ac.publicApplications, ac.privateApplications, ts.prioritizedStatus,ac.nullApplications,
  ac.rejectedAppl
      `;
      setCurrentQuery(query);
      const result = await fetchBaseQuery(query);

      console.log("first", result);
      setReportData(result || []);
    } catch (err) {
      console.error("Report Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  }, [filters, fetchBaseQuery]);

  const filteredData = useMemo(() => {
    if (!admType) return reportData;
    const isPublic = admType.value === "Public";
    return reportData.filter((item) =>
      isPublic ? item.PUBLICAPPLICATIONS > 0 : item.PRIVATEAPPLICATIONS > 0
    );
  }, [admType, reportData]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * entriesLimit;
    const endIndex = startIndex + entriesLimit;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, entriesLimit]);

  const totalPages = useMemo(() => {
    return Math.ceil(filteredData.length / rowsPerPage);
  }, [filteredData]);

  useEffect(() => {
    fetchDropdownOptions();
    fetchReportData();
  }, [filters, fetchReportData]);

  const handleFilterChange = (key) => (selectedOptions) => {
    setFilters((prev) => ({
      ...prev,
      [key]: selectedOptions ? selectedOptions.map((opt) => opt.value) : [],
    }));
  };

  const checkAuthentication = async () => {
    try {
      if (userCode === 0) {
        setAuthState(true);
        return true;
      }

      const checkAdmin = await fetchBaseQuery(
        `SELECT * FROM usermas WHERE UserCode = ${userCode} AND deptcode IN (1, 15, 17, 16, 13, 12)`
      );

      if (Array.isArray(checkAdmin) && checkAdmin.length > 0) {
        setAuthState(true);
        return true;
      }

      setAuthState(false);
      return false;
    } catch (error) {
      console.error("Error checking authentication:", error);
      setAuthState(false);
      return false;
    }
  };
  const tableHeaders = useMemo(
    () => (
      <tr className="text-nowrap table-warning">
        <th># ({reportData.length})</th>
        <th>Student Id</th>
        <th>Student Name</th>
        <th>Student Branch</th>
        <th>Payment Status</th>
        <th>Student Status</th>
        <th>Package</th>
        <th>Category</th>
        <th>CL Status</th>
        <th>Admission Status</th>
        <th>Total Applied</th>
        <th>Total Rejected</th>
        <th>Total Given</th>
        <th>Public</th>
        <th>Private</th>
      </tr>
    ),
    [reportData]
  );


  useEffect(() => {
    checkAuthentication()
  }, [])

  return authState ? (
    <div className="container-fluid bg-light min-vh-100">
      <div className="header py-3">
        <h3 className="display-6 fw-bold">University Dashboard</h3>
      </div>

      <div className="row row-cols-2 row-cols-lg-6 g-2 g-lg-3 mb-2">
        {[
          {
            key: "intake",
            label: "Select Intake",
            options: filterDropdowns.intakeOptions,
          },
          {
            key: "location",
            label: "Select Branch",
            options: filterDropdowns.locationOptions,
          },
          {
            key: "payment",
            label: "Select Payment Status",
            options: filterDropdowns.paymentOptions,
          },
          {
            key: "status",
            label: "Select Admission Status",
            options: filterDropdowns.statusOptions,
          },
          {
            key: "ststatus",
            label: "Select Student Status",
            options: stStatusOptions,
          },
          {
            key: "category",
            label: "Select Category",
            options: filterDropdowns.categoryOptions,
          },
        ].map(({ key, label, options }) => (
          <div className="col-md" key={key} style={{ zIndex: 15 }}>
            <Select
              options={options}
              isMulti
              isClearable
              placeholder={label}
              onChange={handleFilterChange(key)}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 20 }),
              }}
            />
          </div>
        ))}
        <div className="col-md" style={{ zIndex: 15 }}>
          <Select
            options={[
              { value: "Public", label: "Public" },
              { value: "Private", label: "Private" },
            ]}
            onChange={(selectedOption) => setAdmType(selectedOption)}
            isClearable
            placeholder="Select Public/Private"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 20 }),
            }}
          />
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          {/* <div className="d-flex justify-content-end py-2">
            <button
              type="button"
              className="btn btn-sm btn-warning"
              onClick={() =>
                fetchDataByRawQuery(currentQuery, "Confirm to download CSV")
              }
            >
              Export CSV
            </button>
          </div> */}

          <div className="py-3">
            {loading ? (
              <SkeletonTheme highlightColor="#fff3cd">
                <Skeleton height={70} style={{ marginBottom: "10px" }} />
              </SkeletonTheme>
            ) : hasReportData ? (
              <div
                className="table-responsive"
                style={{ maxHeight: "60vh", overflowY: "auto" }}
              >
                <table className="table table-hover table-bordered">
                  <thead
                    className="sticky-top"
                    style={{ zIndex: 10, background: "yellow" }}
                  >
                    {tableHeaders}
                  </thead>
                  <tbody>
                    {paginatedData.map((item, idx) => (
                      <tr key={idx} className="text-nowrap">
                        <td>{(currentPage - 1) * rowsPerPage + idx + 1}</td>
                        <td>{item?.STDID}</td>
                        <td>{item?.STDNAME}</td>
                        <td>{item?.LOCNAME}</td>
                        <td>{item?.PAYMENTSTATUS}</td>
                        <td>{getStudentStatusName(item?.STSTATUS)}</td>
                        <td>{getStudentPackageName(item?.PACKAGECODE)}</td>
                        <td>{item?.CATNAME}</td>
                        <td>{getCourseName(item?.UINLISTSTATUS)}</td>
                        <td>{item?.PRIORITIZEDSTATUS}</td>
                        <td className="text-center">{item?.APPLICATION}</td>
                        <td className="text-center">{item?.REJECTEDAPPL}</td>
                        <td className="text-center">
                          {item?.NULLAPPLICATIONS}
                        </td>
                        <td className="text-center">
                          {item?.PUBLICAPPLICATIONS}
                        </td>
                        <td className="text-center">
                          {item?.PRIVATEAPPLICATIONS}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>No data available.</p>
            )}
          </div>

          {reportData.length > entriesLimit && (
            <div className="d-flex justify-content-center mt-3 col-md-6">
              <ConfigProvider
                theme={{
                  components: {
                    Pagination: {
                      itemActiveBg: "#faf089",
                      itemActiveBgDisabled: "yellow",
                    },
                  },
                  token: {
                    colorBgTextHover: "rgba(255, 246, 164, 0.73)",
                    margin: 0,
                  },
                }}
              >
                <Pagination
                  current={currentPage}
                  pageSize={entriesLimit}
                  total={reportData.length}
                  showSizeChanger
                  showQuickJumper
                  onChange={(page) => setCurrentPage(page)}
                  onShowSizeChange={(current, size) => {
                    setEntriesLimit(size);
                    setCurrentPage(1); // Reset to page 1 when page size changes
                  }}
                />
              </ConfigProvider>
            </div>
          )}
        </div>
      </div>
    </div>
  ) : <NotAuthorize />;
};

export default UniCatDash;
